import { AnalyticsInputWarningIcon } from "./AnalyticsInputWarningIcon";
import {
  ANALYTICS_INPUT_WARNING_TEXT,
  boundToSampleExport,
  sampleReach,
  type BindingTarget,
  type BoundDocumentId,
  type TestTableSourceRow,
} from "../../lib/analyticsFeeds";

/**
 * A document row's sample-column count: how many of the test table's samples
 * hold this file as evidence.
 *
 * One component for both tabs, for the reason the cells beside it share one —
 * a count that read one way on the Documents tab and another on the Support tab
 * would be two answers to a question that has one. Only the long form varies by
 * surface, and over where to go rather than over what is true.
 *
 * A flagged row short of the total is marked, and marked as the same kind of
 * thing the sentence under the table calls it — both read one predicate, so a
 * row cannot be a refusal beside its count and a shortfall in the prose. A row
 * every sample reaches earns no marker: the count already says so, and a marker
 * on every row marks nothing.
 *
 * The marker waits on a parameter bound to the test table's own export as well,
 * which is the second condition the sentence under the table already holds
 * itself to. Both are ordinary mid-authoring states — the flag is settable
 * before any parameter is chosen — and neither is the row's alone to read.
 *
 * The count is drawn whatever the flag and the bindings say and the marker is
 * not, because they answer different questions. How many samples hold a file is
 * true of any file; what its coverage *costs* is a fact about the run, and a run
 * reading no marked file — because none is marked, or because no parameter asks
 * for the marked one — is paying nothing. Marked anyway, it would be an alarm
 * with nothing on the page to explain it: the note below stays silent on both
 * counts, correctly, and an auditor who chased one down and found nothing wrong
 * would learn to discount the marker on the row where it means something.
 */
export function SampleReachCount({
  sourceRows,
  documentId,
  targets,
  flagged,
  hasMatchEditor,
}: {
  sourceRows: readonly TestTableSourceRow[];
  documentId: BoundDocumentId;
  /**
   * The test table's parameters, for whether any of them is answered by the
   * table's own marked export. The list rather than the fact read off it, so
   * this and the sentence under the table cannot come to different readings of
   * the same targets.
   */
  targets: readonly BindingTarget[];
  /** Whether this row is the test table's analytics input. */
  flagged: boolean;
  /**
   * Whether the surface drawing this count also carries the control that
   * changes which samples hold the file. The count is the same number either
   * way; what changes is whether its long form has anywhere to send the
   * auditor.
   */
  hasMatchEditor: boolean;
}) {
  const reach = sampleReach(sourceRows, documentId, hasMatchEditor);
  if (reach === null) return null;
  // One reading for the glyph and for the words that stand in for it, so a
  // marker a reader cannot see is never described as a kind it was not drawn as,
  // and neither is spoken over a run that would not read this file at all.
  const warning = flagged && boundToSampleExport(targets) ? reach.kind : null;
  return (
    <span className="inline-flex items-start gap-1 text-xs text-font-secondary">
      {warning !== null && <AnalyticsInputWarningIcon kind={warning} />}
      {/* The long form spoken rather than left to `title`, which an element with
          no role is not reliably named by, and this cell sits in a grid of divs
          where no heading is associated with it. What the marker means goes with
          it: the glyph is hidden, and two bare numbers do not say what it
          marked. The visible label is the same fact, so it is not said twice. */}
      <span className="sr-only">
        {warning === null
          ? reach.title
          : `${ANALYTICS_INPUT_WARNING_TEXT[warning]} ${reach.title}`}
      </span>
      {/* `title` on the label rather than on the wrapper: a generic element's
          `title` is exposed as an accessible description, and on the wrapper it
          is the same sentence the span above already speaks, announced a second
          time. The pointer is over the fraction either way, which is where the
          tooltip belongs. */}
      <span aria-hidden="true" title={reach.title}>
        {reach.label}
      </span>
    </span>
  );
}
